import { BigNumber, ethers } from "ethers";
import { formatEther, parseEther } from "ethers/lib/utils";
import {
  GetParamsForMintQuery,
  GetParamsForUpdateAnswerWithAttributeQuery,
} from "./schema";
import {
  NenNascentToken__factory as NenNascentTokenFactory,
  NenSeller__factory as NenSellerFactory,
  NenToken__factory as NenTokenFactory,
} from "./typechain";

const hasAttr = ({
  obj,
  key,
}: {
  obj: Record<string, any>;
  key: string;
}): boolean => obj[key] !== undefined;

export const hasWindowEthereum =
  typeof window !== "undefined"
    ? hasAttr({ obj: window, key: "ethereum" })
    : false;

export const openMetamaskApp = () => {
  const domain = window.location.hostname;
  const metamaskAppLink = "https://metamask.app.link/dapp/" + domain;
  window.open(metamaskAppLink, "_self");
};

export const calcProductAsWeiBigNum = (
  numStrInEth: string,
  amount: number
): BigNumber => {
  const parsed = parseEther(numStrInEth);
  return parsed.mul(amount);
};

export const calcProductAsEthStr = (
  numStrInEth: string,
  amount: number
): string => {
  const wei = calcProductAsWeiBigNum(numStrInEth, amount);
  return formatEther(wei);
};

export const getClientBalanceAsWeiBigNum = async (
  web3Provider: ethers.providers.Web3Provider
): Promise<BigNumber> => {
  const signer = web3Provider.getSigner();
  return signer.getBalance();
};

const message =
  "Please sign here. This sign created from your private key will be used to confirm that the user is on the whitelist.";

export const getClientSignature = async (
  web3Provider: ethers.providers.Web3Provider
) => {
  const signer = web3Provider.getSigner();
  return signer.signMessage(message);
};

const answerMessage =
  "Please sign here. This sign created from your private key will be used to confirm that the user is on the gene holder.";

export const getClientAnswerSignature = async (
  web3Provider: ethers.providers.Web3Provider
) => {
  const signer = web3Provider.getSigner();
  return signer.signMessage(answerMessage);
};

const secondMintedMessage =
  "Please sign here. This sign created from your private key will be used to confirm that the user is on the allowlist.";

export const getClientSecondMintedSignature = async (
  web3Provider: ethers.providers.Web3Provider
) => {
  const signer = web3Provider.getSigner();
  return signer.signMessage(secondMintedMessage);
};

export const mint = async (
  web3Provider: ethers.providers.Web3Provider,
  mintCount: number,
  paramsForMint: GetParamsForMintQuery["getParamsForMint"]
) => {
  const nenSeller = await getNenSeller(web3Provider);

  const { v, r, s } = paramsForMint.serverSig;
  await nenSeller.mintNenToken(
    paramsForMint.isWhitelistSale,
    mintCount,
    v,
    r,
    s
  );
};

export const nascentAlMint = async (
  web3Provider: ethers.providers.Web3Provider,
  amount: number,
  proof: string[],
  maxMintableAmount: number,
  value: BigNumber
) => {
  const nenNascentToken = await getNenNascentToken(web3Provider);
  return nenNascentToken.alMint(amount, proof, maxMintableAmount, {
    value,
  });
};

export const nascentPublicMint = async (
  web3Provider: ethers.providers.Web3Provider,
  amount: number,
  value: BigNumber
) => {
  const nenNascentToken = await getNenNascentToken(web3Provider);
  return nenNascentToken.publicMint(amount, { value });
};

export const updateAnswerWithAttribute = async (
  web3Provider: ethers.providers.Web3Provider,
  tokenId: number,
  answer: string,
  credit: string,
  paramsForUpdateAnswerWithAttribute: GetParamsForUpdateAnswerWithAttributeQuery["getParamsForUpdateAnswerWithAttribute"]
) => {
  const nenToken = await getNenToken(web3Provider);

  const a = paramsForUpdateAnswerWithAttribute.attribute;

  const attribute = {
    attributeClothing: ethers.utils.formatBytes32String(a.attributeClothing),
    attributeEyes: ethers.utils.formatBytes32String(a.attributeEyes),
    attributeFace: ethers.utils.formatBytes32String(a.attributeFace),
    attributeHairFront: ethers.utils.formatBytes32String(a.attributeHairFront),
    attributeHairBack: ethers.utils.formatBytes32String(a.attributeHairBack),
    attributeMouth: ethers.utils.formatBytes32String(a.attributeMouth),
    attributeNeck: ethers.utils.formatBytes32String(a.attributeNeck),
    attributeSpecial: ethers.utils.formatBytes32String(a.attributeSpecial),
    attributeSkinTone: ethers.utils.formatBytes32String(a.attributeSkinTone),
    attributeEar: ethers.utils.formatBytes32String(a.attributeEar),
    attributeDna: ethers.utils.formatBytes32String(a.attributeDna),
    question: a.question,
    questionCategory: ethers.utils.formatBytes32String(a.questionCategory),
  };

  const { v, r, s } = paramsForUpdateAnswerWithAttribute.serverSig;
  await nenToken.updateAnswerWithAttribute(
    answer,
    credit,
    { tokenId, attribute },
    v,
    r,
    s
  );
};

export const updateAnswer = async (
  web3Provider: ethers.providers.Web3Provider,
  tokenId: string,
  answer: string,
  credit: string
) => {
  const nenToken = await getNenToken(web3Provider);
  await nenToken.updateAnswer(tokenId, answer, credit);
};

const getNenSeller = async (web3Provider: ethers.providers.Web3Provider) => {
  const nenSellerAddress = process.env.NEXT_PUBLIC_NEN_SELLER_ADDRESS || "";
  if (nenSellerAddress === "") {
    throw new Error("NEXT_PUBLIC_NEN_SELLER_ADDRESS is not set");
  }
  const signer = web3Provider.getSigner();
  const nenSellerFactory = NenSellerFactory.connect(nenSellerAddress, signer);
  const nenSeller = await nenSellerFactory.deployed();
  return nenSeller;
};

const getNenToken = async (web3Provider: ethers.providers.Web3Provider) => {
  const nenTokenAddress = process.env.NEXT_PUBLIC_NEN_TOKEN_ADDRESS || "";
  if (nenTokenAddress === "") {
    throw new Error("NEXT_PUBLIC_NEN_TOKEN_ADDRESS is not set");
  }
  const signer = web3Provider.getSigner();
  const nenTokenFactory = NenTokenFactory.connect(nenTokenAddress, signer);
  const nenToken = await nenTokenFactory.deployed();
  return nenToken;
};

const getNenNascentToken = async (
  web3Provider: ethers.providers.Web3Provider
) => {
  const contractAddress =
    process.env.NEXT_PUBLIC_NEN_NASCENT_TOKEN_ADDRESS || "";
  if (contractAddress === "") {
    throw new Error("NEXT_PUBLIC_NEN_NASCENT_TOKEN_ADDRESS is not set");
  }
  const signer = web3Provider.getSigner();
  const nenNascentTokenFactory = NenNascentTokenFactory.connect(
    contractAddress,
    signer
  );
  return nenNascentTokenFactory.deployed();
};
